import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ProjectSelector } from './ProjectSelector';
import { SessionListPanel } from './SessionListPanel';
import type { SessionRow, SessionSection } from './SessionListPanel';
import { TicketPlanView } from './TicketPlanView';
import { ApprovalBar } from './ApprovalBar';
import { SessionStatus } from './SessionStatus';
import { ChatPanel } from './ChatPanel';
import { Button } from './ui/Button';
import { PageHeader } from './ui/PageHeader';
import { UsageBadge } from './UsageBadge';
import { useHermesSession } from '../hooks/useHermesSession';
import { useFileUpload } from '../hooks/useFileUpload';
import { useWebSocket } from '../hooks/useWebSocket';
import { useApi } from '../hooks/useApi';
import { useProjects } from '../hooks/useProjects';
import { useRefetchOnFocus } from '../hooks/useRefetchOnFocus';
import { initiativeAcceptsAttachments } from '@pantheon/shared';
import type {
  Project,
  WebSocketMessage,
  FileAttachment,
  AthenaInitiative,
  PlanningSession,
  PlanningSessionStatus,
} from '@pantheon/shared';

export interface HermesPlannerProps {
  className?: string;
}

const statusConfig: Record<string, { text: string; dot: string; badge: string }> = {
  drafting: { text: 'Starting', dot: 'bg-gray-300', badge: 'bg-gray-100 text-gray-600' },
  ingesting_context: { text: 'Loading', dot: 'bg-blue-400 animate-pulse', badge: 'bg-blue-50 text-blue-600' },
  generating: { text: 'Generating', dot: 'bg-blue-400 animate-pulse', badge: 'bg-blue-50 text-blue-600' },
  reviewing: { text: 'In Review', dot: 'bg-yellow-400', badge: 'bg-yellow-50 text-yellow-700' },
  approved: { text: 'Approved', dot: 'bg-primary', badge: 'bg-primary-light text-primary' },
  creating_tickets: { text: 'Creating', dot: 'bg-blue-400 animate-pulse', badge: 'bg-blue-50 text-blue-600' },
  completed: { text: 'Completed', dot: 'bg-green-400', badge: 'bg-success-light text-success' },
  failed: { text: 'Failed', dot: 'bg-red-400', badge: 'bg-danger-light text-danger' },
};

const SESSION_GROUPS: { key: string; title: string; statuses: PlanningSessionStatus[] }[] = [
  { key: 'in_progress', title: 'In Progress', statuses: ['drafting', 'ingesting_context', 'generating', 'creating_tickets'] },
  { key: 'in_review', title: 'In Review', statuses: ['reviewing'] },
  { key: 'completed', title: 'Completed', statuses: ['approved', 'completed'] },
  { key: 'failed', title: 'Failed', statuses: ['failed'] },
];

function emailToName(email: string): string {
  return email.split('@')[0].replace(/\./g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function HermesPlanner({ className = '' }: HermesPlannerProps) {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [idea, setIdea] = useState('');
  const [targetBranch, setTargetBranch] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [wsProgress, setWsProgress] = useState<string | null>(null);
  const [initiativeId, setInitiativeId] = useState('');
  const [initiatives, setInitiatives] = useState<AthenaInitiative[]>([]);
  const [creating, setCreating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadFiles, uploading } = useFileUpload();
  const { get, del } = useApi();
  const getRef = useRef(get);
  getRef.current = get;
  const delRef = useRef(del);
  delRef.current = del;

  // Session lists (In Review / Completed) — fetched locally so the project selector
  // is an optional filter: with a project we scope to it, without one we show all
  // projects (the backend returns every project's sessions when no query is passed).
  const [sessions, setSessions] = useState<PlanningSession[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const deletedIdsRef = useRef<Set<string>>(new Set());
  const { projects } = useProjects();
  const projectNameById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const p of projects) m[p.projectId] = p.name;
    return m;
  }, [projects]);

  const selectedProjectId = selectedProject?.projectId;
  const fetchSessions = useCallback(
    async (showLoading = true) => {
      if (showLoading) setListLoading(true);
      const path = selectedProjectId
        ? `/hermes/sessions?projectId=${selectedProjectId}`
        : '/hermes/sessions';
      const result = await getRef.current<PlanningSession[]>(path);
      if (result.success && result.data) {
        setSessions(result.data.filter((s) => !deletedIdsRef.current.has(s.sessionId)));
      }
      if (showLoading) setListLoading(false);
    },
    [selectedProjectId],
  );

  useEffect(() => {
    fetchSessions(true);
  }, [fetchSessions]);

  useEffect(() => {
    const id = setInterval(() => fetchSessions(false), 8000);
    return () => clearInterval(id);
  }, [fetchSessions]);
  const refetchList = useCallback(() => fetchSessions(false), [fetchSessions]);
  useRefetchOnFocus(refetchList);

  const handleDeleteSession = useCallback(async (sessionId: string) => {
    if (!confirm('Delete this session? This cannot be undone.')) return;
    await delRef.current(`/hermes/sessions/${sessionId}`);
    deletedIdsRef.current.add(sessionId);
    setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));
    setTimeout(() => deletedIdsRef.current.delete(sessionId), 120000);
  }, []);

  // Athena plans still open to new sessions (approved, on an integration branch,
  // no Prometheus pre-release yet) — offered as an optional attach target.
  useEffect(() => {
    get<AthenaInitiative[]>('/athena/initiatives')
      .then((res) => {
        if (res.success && res.data) setInitiatives(res.data.filter(initiativeAcceptsAttachments));
      })
      .catch(() => {});
  }, [get]);

  const attachedInitiative = initiatives.find((i) => i.initiativeId === initiativeId);

  const {
    session,
    tickets,
    messages,
    loading,
    error,
    createSession,
    loadSession,
    updateTicket,
    refine,
    regenerate,
    approve,
    stop,
    reset,
  } = useHermesSession();

  // Deep linking: /hermes/:sessionId loads that session, and selecting a session
  // updates the URL so it can be copied and shared.
  const { sessionId: urlSessionId } = useParams();
  const navigate = useNavigate();
  const loadedIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (urlSessionId && urlSessionId !== loadedIdRef.current) {
      loadedIdRef.current = urlSessionId;
      loadSession(urlSessionId);
    }
  }, [urlSessionId, loadSession]);

  // When a session loads (e.g. via a deep link) select its project, so the cross-project
  // summary hides and the session list shows it. Only fills an empty selection — never
  // overrides a project the user picked manually.
  useEffect(() => {
    const pid = session?.projectId;
    if (!pid || pid === selectedProject?.projectId) return;
    void getRef.current<Project>(`/projects/${pid}`).then((r) => {
      if (r.success && r.data) setSelectedProject(r.data);
    });
  }, [session?.projectId, selectedProject?.projectId]);

  const handleWsMessage = useCallback((message: WebSocketMessage) => {
    const data = message.data as Record<string, unknown>;
    switch (message.type) {
      case 'hermes.context.loading':
      case 'hermes.tickets.generating':
        setWsProgress(data.progress as string);
        break;
      case 'hermes.tickets.ready':
      case 'hermes.jira.complete':
        setWsProgress(null);
        break;
      case 'hermes.jira.creating':
        setWsProgress(`Creating ticket ${data.current}/${data.total} in Jira...`);
        break;
      case 'hermes.error':
        setWsProgress(null);
        break;
    }
  }, []);

  useWebSocket({ sessionId: session?.sessionId ?? null, onMessage: handleWsMessage });

  const handleSubmit = async () => {
    if (!selectedProject || !idea.trim()) return;

    let attachments: FileAttachment[] | undefined;
    if (pendingFiles.length > 0) {
      attachments = await uploadFiles(pendingFiles);
      setPendingFiles([]);
    }

    await createSession({
      projectId: selectedProject.projectId,
      idea: idea.trim(),
      additionalContext: additionalContext.trim() || undefined,
      attachments,
      // When attached, the backend overrides targetBranch with the plan's integration branch.
      targetBranch: initiativeId ? undefined : targetBranch.trim() || undefined,
      initiativeId: initiativeId || undefined,
    });
    setCreating(false);
    void fetchSessions(false);
  };

  const handleNewSession = () => {
    reset();
    setIdea('');
    setAdditionalContext('');
    setTargetBranch('');
    setInitiativeId('');
    setPendingFiles([]);
    setWsProgress(null);
    setCreating(false);
    loadedIdRef.current = null;
    if (urlSessionId) navigate('/hermes');
  };

  // Panel "New Session" button — clear any active session and open the create form.
  const handleOpenCreateForm = () => {
    handleNewSession();
    setCreating(true);
  };

  const handleSelectSession = (sessionId: string) => {
    setCreating(false);
    navigate(`/hermes/${sessionId}`);
  };

  // Approve, then refresh the session list so the row moves In Review → Completed
  // without waiting for the next poll.
  const handleApprove = useCallback(async () => {
    await approve();
    void fetchSessions(false);
  }, [approve, fetchSessions]);

  const hasSession = session !== null;
  const isNewSession = !hasSession;
  const showStatus = hasSession && !['reviewing', 'completed', 'failed'].includes(session.status);
  const showTickets = hasSession && tickets.length > 0;
  const showApproval = hasSession && ['reviewing', 'completed', 'failed'].includes(session.status);

  // Group sessions into In Review / Completed rows for the panel.
  const toRow = useCallback(
    (s: PlanningSession): SessionRow => {
      const cfg = statusConfig[s.status] ?? statusConfig.drafting;
      return {
        id: s.sessionId,
        title: s.input.idea,
        statusText: cfg.text,
        statusBadge: cfg.badge,
        statusDot: cfg.dot,
        projectName: projectNameById[s.projectId],
        createdAt: s.createdAt,
        updatedAt: s.updatedAt,
        meta: `Started by ${emailToName(s.userEmail)}`,
        onDelete: () => handleDeleteSession(s.sessionId),
      };
    },
    [projectNameById, handleDeleteSession],
  );

  const sections = useMemo<SessionSection[]>(() => {
    const byRecent = (a: PlanningSession, b: PlanningSession) =>
      new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime();
    return SESSION_GROUPS.map((g) => ({
      key: g.key,
      title: g.title,
      rows: sessions
        .filter((s) => g.statuses.includes(s.status))
        .sort(byRecent)
        .map(toRow),
    }));
  }, [sessions, toRow]);

  return (
    <div className={`max-w-4xl animate-rise ${className}`}>
      <PageHeader
        mark="H"
        markBg="bg-hermes/12"
        markText="text-hermes"
        title="Hermes"
        subtitle="Translate ideas into structured work items."
      />

      {/* Project filter (optional) — scopes the session lists; empty = all projects */}
      <div className="mb-5">
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
          Filter by project
          <span className="text-xs text-gray-500 font-normal ml-2">Optional — leave empty to see every project</span>
        </label>
        <ProjectSelector
          value={selectedProject?.projectId}
          onChange={(p) => setSelectedProject(p)}
        />
      </div>

      {/* Error */}
      {error && (
        <div className="bg-danger-light border border-danger/20 text-danger rounded-xl px-4 py-3 mb-5 text-sm font-medium">
          {error}
        </div>
      )}

      {/* Session lists (In Review / Completed) — hidden while a session is open or the create form is up */}
      {!hasSession && !creating && (
        <SessionListPanel
          sections={sections}
          onSelect={handleSelectSession}
          onNewSession={handleOpenCreateForm}
          showProject={!selectedProject}
          loading={listLoading}
          emptyText="No planning sessions yet."
          className="mb-6"
        />
      )}

      {/* New session input form */}
      {isNewSession && creating && (
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900">New planning session</h3>
            <Button variant="ghost" size="sm" onClick={() => setCreating(false)}>
              ← Back to sessions
            </Button>
          </div>

          {!selectedProject && (
            <div className="text-sm text-gray-600 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2.5">
              Pick a project above to start a new session.
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">What do you want to build?</label>
            <textarea
              value={idea}
              onChange={(e) => setIdea(e.target.value)}
              placeholder="Describe the feature, improvement, or fix you need..."
              rows={6}
              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-y"
            />
          </div>

          {initiatives.length > 0 && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Attach to Athena plan (optional)
                <span className="text-xs text-gray-500 font-normal ml-2">Adds this session to the plan and builds on its integration branch, so a later Argus review covers it</span>
              </label>
              <select
                value={initiativeId}
                onChange={(e) => setInitiativeId(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              >
                <option value="">Standalone — not attached to a plan</option>
                {initiatives.map((i) => {
                  const label = (i.brief || i.initiativeId).replace(/\s+/g, ' ');
                  return (
                    <option key={i.initiativeId} value={i.initiativeId}>
                      {label.slice(0, 70)}{label.length > 70 ? '…' : ''}
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          {attachedInitiative ? (
            <div className="text-sm text-gray-600 bg-primary-light/40 border border-primary/20 rounded-lg px-3 py-2.5">
              Builds on the plan's integration branch
              {attachedInitiative.integrationBranch && (
                <> — <span className="font-mono text-gray-800">{attachedInitiative.integrationBranch}</span></>
              )}
              . It'll appear on the Athena plan and be covered by the next Argus review.
            </div>
          ) : (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Target branch
                <span className="text-xs text-gray-500 font-normal ml-2">Daedalus clones from and PRs into this branch — leave empty for the repo default</span>
              </label>
              <input
                value={targetBranch}
                onChange={(e) => setTargetBranch(e.target.value)}
                placeholder={selectedProject?.repos[0]?.defaultBranch ?? 'develop'}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Additional context (optional)</label>
            <textarea
              value={additionalContext}
              onChange={(e) => setAdditionalContext(e.target.value)}
              placeholder="Links, constraints, technical notes..."
              rows={3}
              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-y"
            />
          </div>

          {/* File attachments */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Attachments (optional)</label>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.txt,.md,.csv,.xls,.xlsx"
              onChange={(e) => setPendingFiles((prev) => [...prev, ...Array.from(e.target.files ?? [])])}
              className="hidden"
            />
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-lg px-4 py-3 text-center cursor-pointer hover:border-primary hover:bg-primary-light/30 transition-colors"
            >
              <p className="text-sm text-gray-500">Click to attach images, PDFs, docs, or spreadsheets</p>
            </div>
            {pendingFiles.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {pendingFiles.map((file, i) => (
                  <span key={i} className="inline-flex items-center gap-1 text-xs bg-primary-light text-primary px-2.5 py-1 rounded-full">
                    {file.name}
                    <button onClick={() => setPendingFiles((prev) => prev.filter((_, j) => j !== i))} className="hover:text-danger ml-0.5">&times;</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <Button onClick={handleSubmit} disabled={!idea.trim() || !selectedProject} loading={loading || uploading} size="lg">
            {uploading ? 'Uploading files...' : 'Generate Ticket Plan'}
          </Button>
        </div>
      )}

      {/* Session content — always rendered once session exists to prevent DOM shifts */}
      {hasSession && (
        <>
          {/* Back to the session list (an open session otherwise has no way back) */}
          <div className="mb-4">
            <Button variant="ghost" size="sm" onClick={handleNewSession}>
              ← Back to sessions
            </Button>
          </div>

          {/* Loading / Processing */}
          <div className={showStatus ? 'my-8 bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50' : 'hidden'}>
            <SessionStatus status={session.status} progress={wsProgress ?? undefined} />
            <div className="flex justify-center pb-4">
              <Button variant="danger" size="sm" onClick={stop}>
                Stop
              </Button>
            </div>
          </div>

          {/* Chat + Tickets */}
          <div className={tickets.length > 0 ? 'mt-2 space-y-5' : 'hidden'}>
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">Refine with Hermes</h3>
              <ChatPanel
                messages={messages}
                onSend={refine}
                loading={loading}
                disabled={!['reviewing', 'failed'].includes(session.status)}
              />
            </div>

            <TicketPlanView
              tickets={tickets}
              summary={session.output?.summary}
              complexity={session.output?.estimatedComplexity}
              onUpdateTicket={updateTicket}
              readOnly={['approved', 'creating_tickets', 'completed'].includes(session.status)}
            />
          </div>

          {/* Approval */}
          <UsageBadge usage={session.usage} className="mt-3" />

          <div className={showApproval ? '' : 'hidden'}>
            <ApprovalBar
              status={session.status}
              ticketCount={tickets.length}
              jiraResults={session.jiraResults}
              onApprove={handleApprove}
              onRegenerate={regenerate}
              onReset={handleNewSession}
              loading={loading}
            />
          </div>
        </>
      )}
    </div>
  );
}
